import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { addTerminalTools, getSymbolProfile, recordLaunch, updateTerminal, removeTerminalTool } from "../lib/api";
import { domainOf, timeAgo, slugify, outboundHref } from "../lib/utils-tv";
import TerminalMenuBar from "./TerminalMenuBar";
import TickerPicker from "./TickerPicker";
import PWAInstallLink from "./PWAInstallLink";
import ExtensionInstallLink from "./ExtensionInstallLink";
import { useOpenMode } from "../lib/pwa";
import MostUsedStrip from "./MostUsedStrip";
import Concierge from "./Concierge";
import AgentExport from "./AgentExport";

const TICKER_KEY = "tv_terminal_ticker";

function TerminalCard({ tool, covered }) {
  return (
    <div className="break-inside-avoid mb-5" data-testid={`terminal-tool-${tool.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <a
          href={outboundHref(tool, "terminal")}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => recordLaunch(tool.id)}
          className="tv-link text-base font-bold break-words"
          data-testid={`terminal-tool-name-${tool.id}`}
          title={`Launch ${tool.title} ↗`}
        >
          {tool.title} <span className="text-[12px] font-normal">↗</span>
        </a>
        <span className="text-[11px] font-bold tabular-nums whitespace-nowrap text-black">
          {tool.pricing || "$?"}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-3 mt-0.5 text-[12px] text-[#999]">
        <span className="truncate">
          {domainOf(tool.url)}
          {covered && (
            <span
              className="ml-2 text-[10px] font-bold text-black bg-[#ffe000] px-1 py-px"
              data-testid={`terminal-tool-covers-${tool.id}`}
            >
              covers ${covered}
            </span>
          )}
        </span>
        {tool.slug && (
          <Link
            to={`/tool/${tool.slug}`}
            className="hover:text-black hover:underline whitespace-nowrap"
            data-testid={`terminal-tool-details-${tool.id}`}
          >
            details
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Shared terminal body — the persistent menu bar, meta row, grouped command
 * deck, and the concierge "add tools" modal. Used by both the public
 * `/terminal/:id` page and the standalone PWA launcher (`/app`).
 */
export default function TerminalView({ id, term, setTerm, catOrder }) {
  const openMode = useOpenMode();
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [conciergeSeed, setConciergeSeed] = useState("");
  const [collapsed, setCollapsed] = useState({}); // category -> open?(bool); absent = default
  const [editMode, setEditMode] = useState(false);
  const [titleDraft, setTitleDraft] = useState(term.title || "");
  const isOwner = !!term.is_owner;
  const [ticker, setTicker] = useState(() => {
    try {
      return localStorage.getItem(TICKER_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    setTitleDraft(term.title || "");
  }, [term.title]);

  const saveTitle = async () => {
    try {
      const updated = await updateTerminal(id, { title: titleDraft.trim() });
      setTerm(updated);
      toast.success("renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "rename failed");
    }
  };

  const moveTool = async (idx, dir) => {
    const ids = term.tools.map((t) => t.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try {
      const updated = await updateTerminal(id, { tool_ids: ids });
      setTerm(updated);
    } catch {
      toast.error("reorder failed");
    }
  };

  const removeTool = async (toolId) => {
    try {
      const updated = await removeTerminalTool(id, toolId);
      setTerm(updated);
      toast.success("removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "remove failed");
    }
  };

  const setActiveTicker = (t) => {
    setTicker(t);
    try {
      if (t) localStorage.setItem(TICKER_KEY, t);
      else localStorage.removeItem(TICKER_KEY);
    } catch {
      /* storage blocked — non-fatal */
    }
  };

  const tickerUp = ticker ? ticker.toUpperCase() : null;
  const coversTicker = (t) =>
    tickerUp &&
    Array.isArray(t.tickers) &&
    t.tickers.some((x) => (x || "").toUpperCase() === tickerUp);

  const groups = useMemo(() => {
    if (!term?.tools) return [];
    const byCat = {};
    for (const t of term.tools) {
      const cat = t.category || "Other";
      (byCat[cat] = byCat[cat] || []).push(t);
    }
    const present = Object.keys(byCat);
    const ordered = [
      ...catOrder.filter((c) => byCat[c]),
      ...present.filter((c) => !catOrder.includes(c)),
    ];
    // Float tools that cover the active ticker to the top of their group.
    return ordered.map((cat) => {
      const tools = [...byCat[cat]];
      if (tickerUp) {
        tools.sort((a, b) => (coversTicker(b) ? 1 : 0) - (coversTicker(a) ? 1 : 0));
      }
      return { cat, tools };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, catOrder, tickerUp]);

  const openConcierge = (seed = "") => {
    setConciergeSeed(seed);
    setConciergeOpen(true);
  };

  // Enrich the ticker with real company context (name + sector/industry) so
  // the concierge can surface vertical-specialist tools instead of generic ones.
  const askAboutTicker = async (t) => {
    let seed = `tools for analyzing $${t}`;
    try {
      const p = await getSymbolProfile(t);
      if (p && p.name) {
        const bits = [p.sector, p.industry].filter(Boolean).join(", ");
        seed = `tools for analyzing ${p.name} ($${t})` + (bits ? ` — ${bits}` : "");
      }
    } catch {
      /* profile unavailable — keep the bare-symbol seed */
    }
    openConcierge(seed);
  };

  const shareUrl = `${window.location.origin}/terminal/${id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("link copied");
    } catch {
      toast.error("could not copy");
    }
  };

  const addToolsToTerminal = async (slugs) => {
    const before = term?.tools?.length || 0;
    try {
      const updated = await addTerminalTools(id, slugs);
      setTerm(updated);
      const added = (updated.tools?.length || 0) - before;
      toast.success(added > 0 ? `added ${added} tool${added === 1 ? "" : "s"}` : "already in terminal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not add tools");
    }
  };

  useEffect(() => {
    if (!conciergeOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setConciergeOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [conciergeOpen]);

  const title = term.title || "my terminal";
  const count = term.tools.length;

  return (
    <>
      <TerminalMenuBar title={title} groups={groups} categoryVocab={catOrder} clusters={term?.clusters} onConcierge={isOwner ? () => openConcierge("") : null} openMode={openMode} />

      <div className="px-1 pt-4" data-testid="terminal-most-used">
        <MostUsedStrip />
      </div>

      <div
        className="px-1 pt-3 pb-2 border-b border-[#eee]"
        data-testid="terminal-ticker-bar"
      >
        <TickerPicker
          value={ticker}
          onChange={setActiveTicker}
          onAsk={askAboutTicker}
        />
      </div>

      <div
        className="px-1 py-3 text-[12px] text-[#666] flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-[#eee]"
        data-testid="terminal-meta"
      >
        <span>
          {count} {count === 1 ? "tool" : "tools"}
        </span>
        <span className="text-[#bbb]">·</span>
        <span>created {timeAgo(term.created_at)}</span>
        <span className="text-[#bbb]">·</span>
        <button type="button" onClick={copyLink} className="tv-link" data-testid="terminal-copy">
          copy link
        </button>
        <span className="text-[#bbb]">·</span>
        <a
          href={`https://x.com/intent/post?text=${encodeURIComponent(
            `${title} — ${count} investing tools`,
          )}&url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="tv-link"
          data-testid="terminal-share-twitter"
        >
          x
        </a>
        <span className="text-[#bbb]">·</span>
        <AgentExport id={term.id} />
        <PWAInstallLink />
        <ExtensionInstallLink />
        {isOwner && (
          <>
            <span className="text-[#bbb]">·</span>
            <button
              type="button"
              onClick={() => setEditMode((v) => !v)}
              className="tv-link font-bold"
              data-testid="terminal-edit-toggle"
            >
              {editMode ? "done editing" : "edit stack"}
            </button>
          </>
        )}
      </div>

      {isOwner && editMode && (
        <div className="px-1 py-3 border-b border-[#eee] flex flex-wrap items-center gap-2" data-testid="terminal-rename">
          <span className="text-[12px] text-[#888]">name:</span>
          <input
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            maxLength={80}
            className="tv-input text-[13px] max-w-xs"
            data-testid="terminal-rename-input"
            placeholder="my stack"
          />
          <button
            type="button"
            onClick={saveTitle}
            className="tv-btn tv-btn-primary text-[12px]"
            data-testid="terminal-rename-save"
          >
            save
          </button>
        </div>
      )}

      {isOwner && editMode ? (
        <div className="mt-6 space-y-2" data-testid="terminal-edit-list">
          <div className="text-[11px] text-[#999] uppercase tracking-[0.15em] border-b border-[#eee] pb-1 mb-2">
            reorder / remove ({term.tools.length})
          </div>
          {term.tools.length === 0 ? (
            <div className="text-[13px] text-[#888] py-4" data-testid="terminal-edit-empty">
              no tools left. add some via the menu bar above.
            </div>
          ) : (
            term.tools.map((t, idx) => (
              <div
                key={t.id}
                className="flex items-center gap-2 border-b border-[#f0f0f0] py-1.5"
                data-testid={`terminal-edit-row-${t.id}`}
              >
                <div className="flex flex-col leading-none">
                  <button
                    type="button"
                    onClick={() => moveTool(idx, -1)}
                    disabled={idx === 0}
                    className="text-[10px] text-[#888] hover:text-black disabled:opacity-30"
                    data-testid={`terminal-move-up-${t.id}`}
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => moveTool(idx, 1)}
                    disabled={idx === term.tools.length - 1}
                    className="text-[10px] text-[#888] hover:text-black disabled:opacity-30"
                    data-testid={`terminal-move-down-${t.id}`}
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>
                <span className="text-[11px] text-[#bbb] tabular-nums w-5 text-right">{idx + 1}.</span>
                <span className="flex-1 text-[13px] font-bold truncate">{t.title}</span>
                <span className="text-[11px] text-[#999] truncate hidden sm:inline">{domainOf(t.url)}</span>
                <button
                  type="button"
                  onClick={() => removeTool(t.id)}
                  className="text-[11px] text-[#b00020] hover:underline font-bold whitespace-nowrap"
                  data-testid={`terminal-remove-${t.id}`}
                >
                  [remove]
                </button>
              </div>
            ))
          )}
        </div>
      ) : (
      <div className="mt-6 space-y-3" data-testid="terminal-tools">
        {groups.map((g) => {
          const hasCovered = tickerUp && g.tools.some((t) => coversTicker(t));
          const isOpen = g.cat in collapsed ? collapsed[g.cat] : hasCovered;
          return (
            <section key={g.cat} data-testid={`terminal-group-${slugify(g.cat)}`}>
              <button
                type="button"
                onClick={() =>
                  setCollapsed((prev) => ({
                    ...prev,
                    [g.cat]: !(g.cat in prev ? prev[g.cat] : hasCovered),
                  }))
                }
                className="w-full flex items-center gap-2 text-[11px] tracking-[0.15em] text-[#999] uppercase border-b border-[#eee] pb-1 mb-3 hover:text-black"
                data-testid={`terminal-group-toggle-${slugify(g.cat)}`}
                aria-expanded={isOpen}
              >
                <span className="text-[9px] w-2">{isOpen ? "▾" : "▸"}</span>
                <span>{g.cat}</span>
                <span className="text-[#bbb]">({g.tools.length})</span>
              </button>
              {isOpen && (
                <div className="columns-1 md:columns-2 gap-12 mb-5">
                  {g.tools.map((t) => (
                    <TerminalCard key={t.id} tool={t} covered={coversTicker(t) ? tickerUp : null} />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
      )}

      {conciergeOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="terminal-concierge-modal"
          onClick={() => setConciergeOpen(false)}
        >
          <div
            className="bg-white border-2 border-black w-full max-w-2xl mt-10 sm:mt-16 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-black pb-2 mb-3">
              <span className="font-bold text-[13px]">
                {">"}_ add tools to {title}
              </span>
              <button
                type="button"
                onClick={() => setConciergeOpen(false)}
                className="tv-link text-[12px]"
                data-testid="terminal-concierge-close"
              >
                [ esc ]
              </button>
            </div>
            <p className="text-[12px] text-[#666] mb-3">
              ask for tools (e.g. &quot;options flow scanners&quot;) and add the
              concierge&apos;s picks straight into this terminal.
            </p>
            <Concierge compact addMode onAddTools={addToolsToTerminal} seedQuery={conciergeSeed} surface="terminal" />
          </div>
        </div>
      )}
    </>
  );
}
